import React, { useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import {
  fetchAccountsAggregation,
  useQueryNameCardinalitiesByAccount,
  useQueryTransactionsSummary,
} from './transactionsSummaryActions';
import {
  ApiAttributeMappingFieldEnum,
  ApiPersonTransactionsSummary,
  ApiTransactionSummaryByAccount,
  GetPersonAccountsAggregationTypeEnum,
} from '../../api';
import { Spinner } from '../../feedback/Spinner';
import Table from 'react-bootstrap/Table';
import { createSearchParams, Link } from 'react-router-dom';
import { formatAmount, formatIncomingAmount, formatOutgoingAmount, isChildField } from '../../schema/helper';
import LoadMoreTable from '../../table/LoadMoreTable';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import {
  DatePicker,
  SalvButton,
  SalvDropdown,
  SalvProgressCircle,
  SalvTextField,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { useMe } from '../../organisation/useMe';
import { InternalNavigation } from '../../navigation/helper';
import { SearchParamKeys } from '../../common/searchParamKeys';
import {
  InternalApiAttributeType,
  InternalApiEntitySchemaFieldType,
  InternalApiEntityType,
  InternalApiExportFileType,
  InternalApiPersonTransactionsSummary,
  InternalApiSearchOperator,
  InternalApiTransactor,
} from '../../api-internal';
import { useQueryEntitySchema } from '../../schema/entitySchemaActions';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { convertToDropdownOption } from '../../common/helper';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import ExportModal from '../../search/ExportModal';
import { transactionsSummaryV2Api } from '../../apis';

dayjs.extend(utc);

interface Timeframe {
  from: Dayjs;
  to: Dayjs;
}

interface TimeframeOption {
  key: string;
  value: Timeframe;
  label: string;
}

const newDate = (year: number, month: number, day: number): Dayjs => {
  return dayjs.utc().year(year).month(month).date(day).startOf('day');
};

const localDayjsToUTC = (localDate: Dayjs): Dayjs => {
  return dayjs.utc(localDate.format('YYYY-MM-DD')).startOf('day');
};

const timeframeOptions = (features: string[], isCounterparty: boolean): TimeframeOption[] => {
  const year = dayjs().utc().year();
  const month = dayjs().utc().month();
  const day = dayjs().utc().date();
  const options = [
    {
      key: 'today',
      value: {
        from: newDate(year, month, day),
        to: newDate(year, month, day + 1),
      },
      label: 'Today',
    },
    {
      key: 'yesterday',
      value: {
        from: newDate(year, month, day - 1),
        to: newDate(year, month, day),
      },
      label: 'Yesterday',
    },
    {
      key: '1-day',
      value: {
        from: newDate(year, month, day - 1),
        to: newDate(year, month, day + 1),
      },
      label: 'Last 1 day',
    },
    {
      key: '7-days',
      value: {
        from: newDate(year, month, day - 6),
        to: newDate(year, month, day + 1),
      },
      label: 'Last 7 days',
    },
    {
      key: '30-days',
      value: {
        from: newDate(year, month, day - 29),
        to: newDate(year, month, day + 1),
      },
      label: 'Last 30 days',
    },
    {
      key: 'this-month',
      value: {
        from: newDate(year, month, 1),
        to: newDate(year, month + 1, 1),
      },
      label: 'This month',
    },
    {
      key: 'last-month',
      value: {
        from: newDate(year, month - 1, 1),
        to: newDate(year, month, 1),
      },
      label: 'Last month',
    },
    {
      key: 'this-year',
      value: {
        from: newDate(year, 0, 1),
        to: newDate(year, 12, 1),
      },
      label: 'This year',
    },
  ];
  if (!isCounterparty && isFeatureEnabled(ORGANISATION_FEATURE.ALL_TIME_FILTER_IN_TRANSACTION_SUMMARY, features)) {
    options.push({
      key: 'all',
      value: {
        from: newDate(year - 100, month, day),
        to: newDate(year, month, day + 1),
      },
      label: 'All time',
    });
  }
  if (isFeatureEnabled(ORGANISATION_FEATURE.TRANSACTION_SUMMARY_V2, features)) {
    options.push({
      key: 'custom',
      value: {
        from: newDate(year, month, day - 1),
        to: newDate(year, month, day),
      },
      label: 'Custom',
    });
  }

  return options;
};

interface Props {
  personId: string;
  isCounterparty: boolean;
}

export const TransactionsSummary = ({ personId, isCounterparty }: Props) => {
  const { features, currentOrganisation } = useMe();
  const defaultFilter = isFeatureEnabled(ORGANISATION_FEATURE.TRANSACTION_SUMMARY_DEFAULT_FILTER_1_DAY, features)
    ? '1-day'
    : '30-days';
  const transactionSummaryV2Enabled = isFeatureEnabled(ORGANISATION_FEATURE.TRANSACTION_SUMMARY_V2, features);
  const [selectedTimeframeOption, setSelectedTimeframeOption] = useState(
    timeframeOptions(features, isCounterparty).find((it) => it.key === defaultFilter) as TimeframeOption
  );
  const [selectedTimeframe, setSelectedTimeframe] = useState<Timeframe>(selectedTimeframeOption.value);
  const [selectedTransactionType, setSelectedTransactionType] = useState<string | undefined>();
  const [senderAccounts, setSenderAccounts] = useState<string[]>([]);
  const [receiverAccounts, setReceiverAccounts] = useState<string[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);

  const {
    data: transactionSchema,
    isPending: isTransactionSchemaPending,
    isError: isTransactionSchemaError,
  } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  const {
    data: summary,
    isPending: isSummaryPending,
    isError: isSummaryError,
  } = useQueryTransactionsSummary(
    personId,
    transactionSummaryV2Enabled,
    selectedTimeframe.from.toDate(),
    selectedTimeframe.to.toDate(),
    selectedTransactionType
  );

  useEffect(() => {
    if (!summary) {
      return;
    }

    setSenderAccounts(summary.byReceived.map((summaryByAcount) => summaryByAcount.account));
    setReceiverAccounts(summary.bySent.map((summaryByAcount) => summaryByAcount.account));
  }, [summary]);

  const {
    data: receiverNameCardinalitiesBySenderAccount,
    isPending: isReceiverNameCardinalitiesBySenderAccountPending,
    isError: isReceiverNameCardinalitiesBySenderAccountError,
    refetch: refetchReceiverNameCardinalitiesByAccount,
  } = useQueryNameCardinalitiesByAccount(senderAccounts, InternalApiTransactor.SENDER);

  const {
    data: senderNameCardinalitiesByReceiverAccount,
    isPending: isSenderNameCardinalitiesByReceiverAccount,
    isError: isSenderNameCardinalitiesBySenderAccountError,
    refetch: refetchSenderNameCardinalitiesByAccount,
  } = useQueryNameCardinalitiesByAccount(receiverAccounts, InternalApiTransactor.RECEIVER);

  useEffect(() => {
    refetchReceiverNameCardinalitiesByAccount();
  }, [senderAccounts.length, refetchReceiverNameCardinalitiesByAccount]);

  useEffect(() => {
    refetchSenderNameCardinalitiesByAccount();
  }, [receiverAccounts.length, refetchSenderNameCardinalitiesByAccount]);

  if (isTransactionSchemaError || isAttributeMappingsError) {
    const resources: string[] = [];
    if (isTransactionSchemaError) {
      resources.push('Schema');
    }
    if (isAttributeMappingsError) {
      resources.push('Attribute Mappings');
    }
    return <DataLoadingErrorFallback message={'Could not load following resources: ' + resources.join(', ')} />;
  } else if (isTransactionSchemaPending || isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Transaction Summary resources'} />;
  }

  const transactionSchemaFields = transactionSchema.schemaFields.filter((schemaField) => !isChildField(schemaField));
  const parentSchemaFields = transactionSchemaFields.filter((schemaField) => !schemaField.parentId);
  const typeField = parentSchemaFields.find((schemaField) => schemaField.fieldName === 'type');
  const attributeMappingMap = attributeMappings.map;

  const renderHeader = () => (
    <tr>
      <th>Account</th>
      <th>Name</th>
      <th className='text-end'>Amount</th>
      <th>Earliest</th>
      <th>Latest</th>
    </tr>
  );

  const getTransactorConfiguration = (transactor: InternalApiTransactor) => {
    switch (transactor) {
      case InternalApiTransactor.SENDER:
        return {
          accountMapping: ApiAttributeMappingFieldEnum.FROM_ACCOUNT,
          nameMapping: ApiAttributeMappingFieldEnum.FROM_NAME,
          nameCardinalitiesByAccount: receiverNameCardinalitiesBySenderAccount?.nameCardinalitiesByAccount,
          isNameCardinalitiesByAccountPending: isReceiverNameCardinalitiesBySenderAccountPending,
          isNameCardinalitiesByAccountError: isReceiverNameCardinalitiesBySenderAccountError,
        };
      case InternalApiTransactor.RECEIVER:
        return {
          accountMapping: ApiAttributeMappingFieldEnum.TO_ACCOUNT,
          nameMapping: ApiAttributeMappingFieldEnum.TO_NAME,
          nameCardinalitiesByAccount: senderNameCardinalitiesByReceiverAccount?.nameCardinalitiesByAccount,
          isNameCardinalitiesByAccountPending: isSenderNameCardinalitiesByReceiverAccount,
          isNameCardinalitiesByAccountError: isSenderNameCardinalitiesBySenderAccountError,
        };
    }
  };

  const renderLoadMoreTableBody = (items: ApiTransactionSummaryByAccount[], transactor: InternalApiTransactor) => {
    const {
      accountMapping,
      nameMapping,
      nameCardinalitiesByAccount,
      isNameCardinalitiesByAccountPending,
      isNameCardinalitiesByAccountError,
    } = getTransactorConfiguration(transactor);

    const accountSchemaField = parentSchemaFields.find(
      (schemaField) => schemaField.fieldName === attributeMappingMap[accountMapping]?.mapping
    )!;

    const nameSchemaField = parentSchemaFields.find(
      (schemaField) => schemaField.fieldName === attributeMappingMap[nameMapping]?.mapping
    )!;

    return items.map((transaction: ApiTransactionSummaryByAccount, index) => {
      const matchingCount = nameCardinalitiesByAccount?.[transaction.account] || 0;
      return (
        <tr key={index}>
          <td>
            <Link
              to={{
                pathname: InternalNavigation.transactionSearch,
                search: createSearchParams({
                  [SearchParamKeys.attributeIdentifier]: accountSchemaField?.id || '',
                  [SearchParamKeys.attributeType]: InternalApiAttributeType.SCHEMA_FIELD,
                  [SearchParamKeys.operator]: InternalApiSearchOperator.IS_EXACTLY,
                  [SearchParamKeys.query]: transaction.account,
                }).toString(),
              }}
            >
              {transaction.account}
            </Link>
            {isNameCardinalitiesByAccountPending ? (
              <SalvProgressCircle ariaLabel={`Loading names that account ${transaction.account} has transacted with`} />
            ) : isNameCardinalitiesByAccountError || matchingCount === -1 ? (
              <SalvTooltipWrapper
                tooltip={`Failed to load the number of customers account ${transaction.account} has transacted with`}
              >
                <FontAwesomeIcon icon={faCircleExclamation} style={{ color: 'var(--text-medium)' }} />
              </SalvTooltipWrapper>
            ) : (
              matchingCount > 1 && <p className='matching-accounts'>{`(${matchingCount - 1} other customers)`}</p>
            )}
          </td>
          <td>
            <Link
              to={{
                pathname: InternalNavigation.transactionSearch,
                search: createSearchParams({
                  [SearchParamKeys.attributeIdentifier]: nameSchemaField?.id || '',
                  [SearchParamKeys.attributeType]: InternalApiAttributeType.SCHEMA_FIELD,
                  [SearchParamKeys.operator]: InternalApiSearchOperator.IS_EXACTLY,
                  [SearchParamKeys.query]: transaction.name,
                }).toString(),
              }}
            >
              {transaction.name}
            </Link>
          </td>
          {transactor === InternalApiTransactor.SENDER ? (
            <td className='text-end'>{formatIncomingAmount(transaction.totalAmount, currentOrganisation?.currency)}</td>
          ) : (
            <td className='text-end amount-outgoing'>
              {formatOutgoingAmount(transaction.totalAmount, currentOrganisation?.currency)}
            </td>
          )}
          <td>{<FormattedDateTime date={transaction.firstTime} />}</td>
          <td>{<FormattedDateTime date={transaction.lastTime} />}</td>
        </tr>
      );
    });
  };

  const fetchPage = async (offset: number, size: number, type: GetPersonAccountsAggregationTypeEnum) => {
    const result = await fetchAccountsAggregation(
      personId,
      offset,
      size,
      type,
      transactionSummaryV2Enabled,
      selectedTimeframe.from.toDate(),
      selectedTimeframe.to.toDate()
    );

    if (result) {
      const accounts = result.map((summaryByAccount) => summaryByAccount.account);
      if (type === GetPersonAccountsAggregationTypeEnum.RECIPIENT) {
        setSenderAccounts((prev) => [...prev, ...accounts]);
      } else {
        setReceiverAccounts((prev) => [...prev, ...accounts]);
      }
    }

    return result;
  };

  function onTimeframeChange(value: TimeframeOption) {
    setSelectedTimeframeOption(value);
    setSelectedTimeframe(value.value!);
  }

  const renderByMonth = (summary: ApiPersonTransactionsSummary | InternalApiPersonTransactionsSummary) => (
    <React.Fragment>
      <Card.Header>
        <h4 className='card-header-title'>By month</h4>
      </Card.Header>
      <Table hover size='sm' className='card-table'>
        <thead>
          <tr>
            <th>Period</th>
            <th className='text-end'>Amount</th>
          </tr>
        </thead>
        <tbody>
          {summary.byMonth.map((row, i) => (
            <tr key={i}>
              <td>
                {row.startOfMonth
                  ? row.startOfMonth.getUTCFullYear() +
                    ' ' +
                    row.startOfMonth.toLocaleString('en', {
                      month: 'long',
                      timeZone: 'UTC',
                    })
                  : '-'}
              </td>
              <td className='text-end'>{formatAmount(row.totalAmount, currentOrganisation?.currency)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </React.Fragment>
  );

  const renderBody = (summary: ApiPersonTransactionsSummary | InternalApiPersonTransactionsSummary) => (
    <>
      <Row className='align-items-center'>
        <Col>
          <h5 className='mb-0'>Total funds sent</h5>
        </Col>
        <Col md='auto'>
          <span className='small amount-outgoing'>
            {formatOutgoingAmount(summary.totalSentAmount, currentOrganisation?.currency)}
          </span>
        </Col>
      </Row>
      <Row className='align-items-center'>
        <Col>
          <h5 className='mb-0'>Total funds received</h5>
        </Col>
        <Col md='auto'>
          <span className='small'>
            {formatIncomingAmount(summary.totalReceivedAmount, currentOrganisation?.currency)}
          </span>
        </Col>
      </Row>
      {'totalSentTransactions' in summary && summary.totalSentTransactions !== null && (
        <Row className='align-items-center'>
          <Col>
            <h5 className='mb-0'>Total transactions sent</h5>
          </Col>
          <Col md='auto'>
            <span className='small'>{summary.totalSentTransactions}</span>
          </Col>
        </Row>
      )}
      {'totalSentTransactions' in summary && summary.totalSentTransactions !== null && (
        <Row className='align-items-center'>
          <Col>
            <h5 className='mb-0'>Total transactions received</h5>
          </Col>
          <Col md='auto'>
            <span className='small'>{summary.totalReceivedTransactions}</span>
          </Col>
        </Row>
      )}
      <Row className='align-items-center'>
        <Col>
          <h5 className='mb-0'>Average funds sent</h5>
        </Col>
        <Col md='auto'>
          <span className='small amount-outgoing'>
            {formatOutgoingAmount(summary.averageSentAmount, currentOrganisation?.currency)}
          </span>
        </Col>
      </Row>
      <Row className='align-items-center'>
        <Col>
          <h5 className='mb-0'>Average funds received</h5>
        </Col>
        <Col md='auto'>
          <span className='small'>
            {formatIncomingAmount(summary.averageReceivedAmount, currentOrganisation?.currency)}
          </span>
        </Col>
      </Row>
      {summary.firstTime && (
        <Row className='align-items-center'>
          <Col>
            <h5 className='mb-0'>Earliest</h5>
          </Col>
          <Col md='auto'>
            <span className='small'>
              <FormattedDateTime date={summary.firstTime} />
            </span>
          </Col>
        </Row>
      )}
      {summary.lastTime && (
        <Row className='align-items-center'>
          <Col>
            <h5 className='mb-0'>Latest</h5>
          </Col>
          <Col md='auto'>
            <span className='small'>
              <FormattedDateTime date={summary.lastTime} />
            </span>
          </Col>
        </Row>
      )}
      <LoadMoreTable
        key={`${selectedTimeframeOption.key}-sender-${isSummaryPending}`}
        title='Sender accounts'
        initialItems={summary.byReceived}
        header={renderHeader}
        body={(items) => renderLoadMoreTableBody(items, InternalApiTransactor.SENDER)}
        borderless
        total={summary.totalReceivedAccounts}
        showLoadMoreUntilNoDataToFetch={true}
        page={0}
        size={5}
        fetchPage={(offset, size) => fetchPage(offset, size, GetPersonAccountsAggregationTypeEnum.RECIPIENT)}
      />
      <LoadMoreTable
        key={`${selectedTimeframeOption.key}-recipient-${isSummaryPending}`}
        title='Recipient accounts'
        initialItems={summary.bySent}
        header={renderHeader}
        body={(items) => renderLoadMoreTableBody(items, InternalApiTransactor.RECEIVER)}
        borderless
        total={summary.totalSentAccounts}
        showLoadMoreUntilNoDataToFetch={true}
        page={0}
        size={5}
        fetchPage={(offset, size) => fetchPage(offset, size, GetPersonAccountsAggregationTypeEnum.SENDER)}
      />
      {summary.byMonth.length > 1 && renderByMonth(summary)}
    </>
  );

  const exportSummary = async (fileType: InternalApiExportFileType) => {
    return await transactionsSummaryV2Api.getPersonTransactionsSummaryExport({
      personId: personId,
      from: selectedTimeframe.from.toDate(),
      to: selectedTimeframe.to.toDate(),
      transactionType: selectedTransactionType,
      fileType: fileType,
    });
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  return (
    <React.Fragment>
      {summary && 'apiDisabled' in summary && summary.apiDisabled && !isSummaryPending ? (
        <Card>
          <Card.Header>
            <h4 className='card-header-title'>Transactions summary</h4>
          </Card.Header>
          <Card.Body>
            <div className='small'>
              Sorry, transactions summary is currently unavailable for this Service Provider due to the large amount of
              data related to them.
              <br /> We’re working hard on a new feature that will fix this issue.
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h4 className='card-header-title'>Transactions summary</h4>
            {isFeatureEnabled(ORGANISATION_FEATURE.TRANSACTION_SUMMARY_EXPORT, features) && (
              <SalvButton label={'Export'} onClick={handleExportClick} size={Size.SMALL} variant={'secondary'} />
            )}
          </Card.Header>

          <Card.Body>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '8px' }}>
              <SalvDropdown
                label={'Time range'}
                ariaLabel={'Time range'}
                size={Size.SMALL}
                options={timeframeOptions(features, isCounterparty).map((tfOption) => {
                  return { key: tfOption.key, value: tfOption.key, label: tfOption.label };
                })}
                value={{
                  key: selectedTimeframeOption.key,
                  value: selectedTimeframeOption.key,
                  label: selectedTimeframeOption.label,
                }}
                clearable={false}
                onChange={([option]) => {
                  onTimeframeChange(
                    timeframeOptions(features, isCounterparty).find((tfOption) => tfOption.key === option.value)!
                  );
                }}
              />
              {selectedTimeframeOption.key === 'custom' && (
                <>
                  <div style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                    <DatePicker
                      size={Size.SMALL}
                      value={selectedTimeframe.from}
                      showDefaultOptions={false}
                      isClearable={false}
                      onChange={(val: Dayjs | null) => {
                        if (val) {
                          const newFromDate = localDayjsToUTC(val);
                          if (!selectedTimeframe.from.isSame(newFromDate, 'day')) {
                            setSelectedTimeframe({
                              from: newFromDate,
                              to: selectedTimeframe.to,
                            });
                          }
                        }
                      }}
                      min={dayjs().subtract(1, 'year')}
                      max={selectedTimeframe.to}
                    />
                  </div>
                  <DatePicker
                    size={Size.SMALL}
                    value={selectedTimeframe.to}
                    showDefaultOptions={false}
                    isClearable={false}
                    onChange={(val: Dayjs | null) => {
                      if (val) {
                        const newToDate = localDayjsToUTC(val);
                        if (!selectedTimeframe.to.isSame(newToDate, 'day')) {
                          setSelectedTimeframe({
                            from: selectedTimeframe.from,
                            to: newToDate,
                          });
                        }
                      }
                    }}
                    min={selectedTimeframe.from}
                    max={dayjs()}
                  />
                </>
              )}
            </div>
            {transactionSummaryV2Enabled &&
              selectedTimeframeOption.key !== 'all' &&
              typeField?.type !== InternalApiEntitySchemaFieldType.ENUM && (
                <div style={{ display: 'flex', paddingBottom: '24px' }}>
                  <SalvTextField
                    label={'Transaction type'}
                    ariaLabel={'Transaction type'}
                    size={Size.SMALL}
                    placeholder='Type...'
                    onChange={setSelectedTransactionType}
                    value={selectedTransactionType}
                  />
                </div>
              )}
            {transactionSummaryV2Enabled &&
              selectedTimeframeOption.key !== 'all' &&
              typeField?.type === InternalApiEntitySchemaFieldType.ENUM && (
                <div style={{ display: 'flex', paddingBottom: '24px' }}>
                  <SalvDropdown
                    label={'Transaction type'}
                    ariaLabel={'Transaction type'}
                    size={Size.SMALL}
                    onChange={([option]) => setSelectedTransactionType(option?.key)}
                    value={
                      selectedTransactionType
                        ? convertToDropdownOption(selectedTransactionType, selectedTransactionType)
                        : undefined
                    }
                    options={typeField?.enumOptions?.map((option) => convertToDropdownOption(option, option)) || []}
                  />
                </div>
              )}
            {summary && !isSummaryPending ? (
              renderBody(summary)
            ) : isSummaryError ? (
              <div className='small'>Failed to load transactions summary. Please try again.</div>
            ) : (
              <Row className='justify-content-center'>
                <Spinner loading />
              </Row>
            )}
          </Card.Body>
        </Card>
      )}
      <ExportModal
        show={showExportModal}
        setShow={setShowExportModal}
        exportEntities={exportSummary}
        synchronous={true}
      />
    </React.Fragment>
  );
};

export default TransactionsSummary;
